import { useCallback, useEffect, useRef, useState } from "react";

import { useLocalStorageState } from "@/hooks";
import { LOCAL_STORAGE_KEYS } from "@/hooks/useLocalStorageState";

const DEFAULT_WIDTH = 320;
const MIN_WIDTH = 280;
const MAX_WIDTH = 800;

interface UseObservabilitySidebarResizingOptions {
  minWidth?: number;
  maxWidth?: number;
}

export const useObservabilitySidebarResizing = ({
  minWidth = MIN_WIDTH,
  maxWidth = MAX_WIDTH,
}: UseObservabilitySidebarResizingOptions = {}) => {
  const [sidebarWidth, setSidebarWidth] = useLocalStorageState<number>(
    LOCAL_STORAGE_KEYS.OBSERVABILITY_SIDEBAR_WIDTH,
    DEFAULT_WIDTH,
  );
  const [isResizing, setIsResizing] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const activeMinWidth = useRef(minWidth);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const diffX = e.pageX - startX.current;
      const newWidth = Math.min(
        Math.max(startWidth.current + diffX, activeMinWidth.current),
        maxWidth,
      );
      setSidebarWidth(newWidth);
    },
    [maxWidth, setSidebarWidth],
  );

  const stopResizing = useCallback(() => {
    setIsResizing(false);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, [handleMouseMove]);

  const startResizing = useCallback(
    (e: React.MouseEvent, currentWidth: number, resizeMinWidth: number) => {
      e.preventDefault();
      e.stopPropagation();

      activeMinWidth.current = resizeMinWidth;
      setIsResizing(true);
      startX.current = e.pageX;
      startWidth.current = currentWidth;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", stopResizing);
    },
    [handleMouseMove, stopResizing],
  );

  useEffect(
    () => () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    },
    [handleMouseMove, stopResizing],
  );

  return {
    sidebarWidth,
    isResizing,
    startResizing,
    minWidth,
    maxWidth,
  };
};
